import React, { useEffect, useState } from 'react';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import config from '../../config';
import Helper from '../../helpers';

const baseUrl = config.apiBaseUrl;

const money = (amount) => `£${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const longDate = (value) => new Date(value).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
const shortDate = (value) => new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

// Bar heights as a percentage of the biggest run day, never below a small stub so a real payment is visible
export const barPercentages = (runDays) => {
    const max = Math.max(...runDays.map(d => d.expectedAmount), 0);
    if (max <= 0) return runDays.map(() => 0);
    return runDays.map(d => Math.max(8, Math.round((d.expectedAmount / max) * 100)));
};

// Pure display of the report, so it can be tested without any network.
// Everything here is EXPECTED money for members who signed up through the site.
export const UpcomingPaymentsView = ({ report }) => {
    const next = report?.nextRunDay;
    const runDays = report?.runDays || [];
    const heights = barPercentages(runDays);

    return (
        <div className="card-luxury p-4 sm:p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center shadow-luxury">
                        <BanknotesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div>
                        <h3 className="text-base sm:text-lg font-display font-semibold text-accent-platinum">Money on your next run day</h3>
                        <p className="text-xs text-emerald-600/70 font-body">Members who signed up through the site</p>
                    </div>
                </div>
                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-900/30 text-amber-300 border border-amber-700/40 whitespace-nowrap">
                    Expected
                </span>
            </div>

            {next ? (
                <>
                    <div className="mb-5">
                        <p className="text-3xl sm:text-4xl font-display font-bold text-accent-platinum" data-testid="next-amount">{money(next.expectedAmount)}</p>
                        <p className="text-sm text-emerald-300 font-body">
                            expected on <span className="font-semibold" data-testid="next-date">{longDate(next.date)}</span>
                            {' '}from {next.memberCount} member{next.memberCount === 1 ? '' : 's'}
                        </p>
                    </div>

                    {runDays.length > 1 && (
                        <div>
                            <p className="text-xs text-emerald-600/70 font-body mb-2">Next run days</p>
                            <div className="flex items-end gap-2 h-28" role="list">
                                {runDays.map((day, i) => (
                                    <div key={day.date} role="listitem" className="flex-1 min-w-0 flex flex-col items-center justify-end h-full"
                                         title={`${longDate(day.date)}: ${money(day.expectedAmount)} expected from ${day.memberCount} member${day.memberCount === 1 ? '' : 's'}`}>
                                        <span className="text-[10px] sm:text-xs text-accent-platinum font-body mb-1">{money(day.expectedAmount)}</span>
                                        <div className="w-full rounded-t bg-gradient-to-t from-emerald-700 to-emerald-500" style={{ height: `${heights[i] * 0.6}%` }} />
                                        <span className="text-[10px] sm:text-xs text-emerald-600/70 font-body mt-1">{shortDate(day.date)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <p className="text-sm text-gray-300 font-body" data-testid="no-run-day">
                    No run day to show yet. Members who sign up through the site appear here once their plan has a run date.
                    Set a run date on the plan in Subscriptions.
                </p>
            )}

            {report?.membersWithoutRunDate > 0 && (
                <p className="mt-4 text-xs text-amber-200/80 font-body" data-testid="without-run-date">
                    {report.membersWithoutRunDate} member{report.membersWithoutRunDate === 1 ? '' : 's'} ({money(report.expectedPerCycleWithoutRunDate)} each cycle)
                    {' '}are on a plan with no run date, so their payment day is not shown. Set a run date on their plan to include them.
                </p>
            )}

            <p className="mt-4 text-xs text-emerald-600/60 font-body">
                Expected money worked out from plan prices. It is not confirmed by GoCardless.
            </p>
        </div>
    );
};

// venueKey is the venue the admin has selected on the dashboard. Admins of several venues must see the
// money for the venue they picked, not for whichever venue happens to be their own first one.
const UpcomingPaymentsCard = ({ venueKey }) => {
    const [report, setReport] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                setReport(null);
                setFailed(false);
                const venue = venueKey || await Helper.getUserVenueId();
                if (!venue) return;
                const token = await Helper.getCookie('wh-jwt');
                const response = await fetch(`${baseUrl}/api/venues/${venue}/reports/upcoming-payments`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    credentials: 'include'
                });
                // The admin may have switched venue while this was loading, so ignore an answer that is out of date
                if (cancelled) return;
                if (response.ok) {
                    setReport(await response.json());
                } else {
                    setFailed(true);
                }
            } catch (error) {
                console.error('Error loading upcoming payments:', error);
                if (!cancelled) setFailed(true);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [venueKey]);

    if (failed) return null; // a dashboard card must never break the page
    if (!report) return null;
    return <UpcomingPaymentsView report={report} />;
};

export default UpcomingPaymentsCard;
